import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';

const DEPARTMENTS = [
  {
    label: 'Flex Printing & Design',
    icon: 'fa-solid fa-print',
    color: '#8B5CF6',
    links: [
      { label: 'Flex & Banners', to: '/print#flex' },
      { label: 'Visiting Cards', to: '/print#cards' },
      { label: 'Brochures & Flyers', to: '/print#brochures' },
      { label: 'Stationery & Packaging', to: '/print#stationery' },
    ],
  },
  {
    label: 'Digital Marketing',
    icon: 'fa-solid fa-chart-line',
    color: '#0A8FDC',
    links: [
      { label: 'SEO & Google Ranking', to: '/services/digital-growth#section-0' },
      { label: 'Google & Meta Ads (PPC)', to: '/services/digital-growth#section-1' },
      { label: 'Social Media Marketing', to: '/services/digital-growth#section-2' },
      { label: 'Marketing Automation', to: '/services/digital-growth#section-3' },
    ],
  },
  {
    label: 'Photography & Video',
    icon: 'fa-solid fa-camera',
    color: '#FF5C00',
    links: [
      { label: 'Wedding Photography', to: '/photography#wedding' },
      { label: 'Event Photography', to: '/photography#events' },
      { label: 'Live Streaming', to: '/photography#streaming' },
      { label: 'Videography & Reels', to: '/photography#videography' },
    ],
  },
  {
    label: 'Branding & Web',
    icon: 'fa-solid fa-pen-nib',
    color: '#10b981',
    links: [
      { label: 'Brand Identity & Logo', to: '/services/branding' },
      { label: 'Web Development', to: '/services/web-development' },
      { label: 'E-Commerce Platforms', to: '/services/web-development#section-1' },
      { label: 'Business Automation', to: '/services/web-development#section-3' },
    ],
  },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [activeDept, setActiveDept] = useState(0);

  const location = useLocation();
  const mobileNavRef = useRef(null);
  const menuBtnRef = useRef(null);
  const megaMenuRef = useRef(null);
  const megaMenuTimerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMegaMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  // Close mega menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (megaMenuRef.current && !megaMenuRef.current.contains(e.target)) {
        setIsMegaMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen || !mobileNavRef.current) return;
    const nav = mobileNavRef.current;
    const focusableEls = nav.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])');
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];
    const trapFocus = (e) => {
      if (e.key === 'Escape') { setIsMobileMenuOpen(false); menuBtnRef.current?.focus(); return; }
      if (e.key !== 'Tab') return;
      if (e.shiftKey) { if (document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); } }
      else { if (document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); } }
    };
    nav.addEventListener('keydown', trapFocus);
    firstEl?.focus();
    return () => nav.removeEventListener('keydown', trapFocus);
  }, [isMobileMenuOpen]);

  const handleAuditClick = useCallback(() => {
    window.dispatchEvent(new Event('openLeadMagnet'));
    setIsMobileMenuOpen(false);
  }, []);

  const isServicesActive = location.pathname.startsWith('/services') ||
    location.pathname.startsWith('/photography') ||
    location.pathname.startsWith('/print');

  const handleServicesEnter = () => {
    clearTimeout(megaMenuTimerRef.current);
    setIsMegaMenuOpen(true);
  };
  const handleServicesLeave = () => {
    megaMenuTimerRef.current = setTimeout(() => setIsMegaMenuOpen(false), 200);
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="announcement-bar">
        <div className="container announcement-inner">
          <span className="announcement-icon-wrap">
            <i className="fa-solid fa-screwdriver-wrench announcement-icon"></i>
          </span>
          <span className="announcement-text">
            Our physical creative studio is under construction & opening soon at Kalarcode, Alappuzha! We are fully operational online & on-site.
          </span>
          <span className="announcement-badge">Starting Soon</span>
        </div>
      </div>
      <div className="nav-wrapper">
        <div className="container nav-container">
          <div className="logo">
            <Link to="/">
              <img src="/logo.svg" alt="Grandframe Creative Digital" className="brand-logo" />
            </Link>
          </div>

          <nav className="desktop-nav">
            <ul className="nav-links">
              <li><Link to="/" className={location.pathname === '/' ? 'active' : ''}>Home</Link></li>

              {/* Services with Mega Menu */}
              <li
                ref={megaMenuRef}
                style={{ position: 'relative' }}
                onMouseEnter={handleServicesEnter}
                onMouseLeave={handleServicesLeave}
              >
                <Link
                  to="/services"
                  className={`nav-services-btn ${isServicesActive ? 'active' : ''} ${isMegaMenuOpen ? 'mega-open' : ''}`}
                  onClick={() => setIsMegaMenuOpen(false)}
                  aria-expanded={isMegaMenuOpen}
                  aria-haspopup="true"
                >
                  Services
                  <i className={`fa-solid fa-chevron-down mega-chevron ${isMegaMenuOpen ? 'rotated' : ''}`}></i>
                </Link>

                {isMegaMenuOpen && (
                  <div className="mega-menu" role="menu">
                    {/* Department tab selectors */}
                    <div className="mega-dept-tabs">
                      {DEPARTMENTS.map((dept, i) => (
                        <button
                          key={dept.label}
                          type="button"
                          className={`mega-dept-tab ${activeDept === i ? 'active' : ''}`}
                          style={{ '--dept-color': dept.color }}
                          onMouseEnter={() => setActiveDept(i)}
                          onClick={() => setActiveDept(i)}
                        >
                          <i className={dept.icon} style={{ color: activeDept === i ? dept.color : '#64748B' }}></i>
                          <span>{dept.label}</span>
                        </button>
                      ))}
                    </div>

                    {/* Active dept links */}
                    <div className="mega-dept-links">
                      <div className="mega-dept-header">
                        <i className={DEPARTMENTS[activeDept].icon} style={{ color: DEPARTMENTS[activeDept].color, fontSize: '1.2rem' }}></i>
                        <span style={{ color: DEPARTMENTS[activeDept].color, fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          {DEPARTMENTS[activeDept].label}
                        </span>
                      </div>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {DEPARTMENTS[activeDept].links.map(link => (
                          <li key={link.to}>
                            <Link
                              to={link.to}
                              className="mega-link"
                              onClick={() => setIsMegaMenuOpen(false)}
                            >
                              <i className="fa-solid fa-chevron-right" style={{ fontSize: '0.6rem', color: DEPARTMENTS[activeDept].color, opacity: 0.7 }}></i>
                              {link.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                      <div className="mega-view-all">
                        <Link to="/services" onClick={() => setIsMegaMenuOpen(false)} style={{ color: '#64748B', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          View all services <i className="fa-solid fa-arrow-right" style={{ fontSize: '0.7rem' }}></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </li>

              <li><Link to="/pricing" className={location.pathname === '/pricing' ? 'active' : ''}>Pricing</Link></li>
              <li><Link to="/about" className={location.pathname === '/about' ? 'active' : ''}>About</Link></li>
              <li><Link to="/blog" className={location.pathname.startsWith('/blog') ? 'active' : ''}>Blog</Link></li>
              <li><Link to="/contact" className={location.pathname === '/contact' ? 'active' : ''}>Contact</Link></li>
            </ul>
          </nav>

          <div className="nav-actions">
            <Link to="/contact" className="btn btn-primary theme-btn nav-cta-btn">Contact Us <i className="fa-solid fa-arrow-right"></i></Link>
            <button
              ref={menuBtnRef}
              className="mobile-menu-btn"
              aria-label="Toggle navigation menu"
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-nav"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              type="button"
            >
              <i className={`fa-solid ${isMobileMenuOpen ? 'fa-xmark' : 'fa-bars'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <div
        id="mobile-nav"
        ref={mobileNavRef}
        className={`mobile-nav ${isMobileMenuOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <ul className="mobile-nav-links">
          <li><Link to="/">Home</Link></li>
          <li style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '0.5rem', marginBottom: '0.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0.5rem 0' }}>Services</span>
            <Link to="/services" style={{ color: '#FF5C00', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', textDecoration: 'none' }}>View all services</Link>
          </li>
          <li style={{ paddingLeft: '0.5rem' }}><Link to="/print"><i className="fa-solid fa-print" style={{ color: '#8B5CF6', marginRight: '8px', width: '16px' }}></i>Flex Printing & Design</Link></li>
          <li style={{ paddingLeft: '0.5rem' }}><Link to="/services/digital-growth"><i className="fa-solid fa-chart-line" style={{ color: '#0A8FDC', marginRight: '8px', width: '16px' }}></i>Digital Marketing</Link></li>
          <li style={{ paddingLeft: '0.5rem' }}><Link to="/photography"><i className="fa-solid fa-camera" style={{ color: '#FF5C00', marginRight: '8px', width: '16px' }}></i>Photography & Video</Link></li>
          <li style={{ paddingLeft: '0.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '0.75rem' }}><Link to="/services/branding"><i className="fa-solid fa-pen-nib" style={{ color: '#10b981', marginRight: '8px', width: '16px' }}></i>Branding & Web</Link></li>
          <li><Link to="/pricing">Pricing</Link></li>
          <li><Link to="/about">About</Link></li>
          <li><Link to="/blog">Blog</Link></li>
          <li><Link to="/contact">Contact</Link></li>
        </ul>
      </div>
    </header>
  );
};

export default Header;
